import React, { useEffect, useMemo, useState } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

import { GoogleMapsOverlay as DeckOverlay } from "@deck.gl/google-maps";
import { PathLayer } from "@deck.gl/layers";
import { ScenegraphLayer } from "@deck.gl/mesh-layers";

import { load } from "@loaders.gl/core";
import { GLTFLoader } from "@loaders.gl/gltf";

import TripBuilder from "./tripBuilder";
import carModel from "../../../../assets/files/models/low_poly_car_001.glb";
import { VehicleWithPositionsType } from "../../../../types/types";

/* =========================
   TIPOS BASE
========================= */
type LngLat = [number, number];

type Frame = {
  point: LngLat;
  heading: number;
};

/* =========================
   CONFIG PERF / CUSTO
========================= */
const DISTANCE_LIMIT_METERS = 80;
const GOOGLE_POINT_LIMIT = 8;

const CACHE_LIMIT = 100;
const CACHE_TTL = 1000 * 60 * 10; // 10 min

/* =========================
   CACHE LEVE (FRONT)
========================= */
type CacheItem = {
  value: LngLat[];
  expires: number;
};

const routeCache = new Map<string, CacheItem>();

function getCache(key: string): LngLat[] | null {
  const item = routeCache.get(key);
  if (!item) return null;

  if (Date.now() > item.expires) {
    routeCache.delete(key);
    return null;
  }
  return item.value;
}

function setCache(key: string, value: LngLat[]): void {
  if (routeCache.size >= CACHE_LIMIT) {
    const firstKey = routeCache.keys().next()?.value!!;
    routeCache.delete(firstKey);
  }
  routeCache.set(key, {
    value,
    expires: Date.now() + CACHE_TTL,
  });
}

/* =========================
   HELPERS LEVES
========================= */
function distanceMeters(a: LngLat, b: LngLat): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy) * 111_000;
}

function needsGoogleFix(path: LngLat[]): boolean {
  for (let i = 1; i < path.length; i++) {
    if (distanceMeters(path[i - 1], path[i]) > DISTANCE_LIMIT_METERS) {
      return true;
    }
  }
  return false;
}

function samplePoints(path: LngLat[], max = GOOGLE_POINT_LIMIT): LngLat[] {
  if (path.length <= max) return path;
  const step = Math.floor(path.length / max);
  return path.filter((_, i) => i % step === 0);
}

/* =========================
   PROPS
========================= */
type Props = {
  data: VehicleWithPositionsType;
  tracking?: boolean;
  showPaths?: boolean;
  play?: boolean;
};

/* =========================
   COMPONENTE
========================= */
const LiveRoutesTracker: React.FC<Props> = ({
  data,
  tracking = true,
  showPaths = true,
  play = true,
}) => {
  const map = useMap();
  const routesLib = useMapsLibrary("routes");

  const [paths, setPaths] = useState<LngLat[][]>([]);

  /* ===== GPS BASE (ZERO CUSTO) ===== */
  const gpsPath = useMemo<LngLat[]>(() => {
    if (!Array.isArray(data?.positions)) return [];
    return data.positions
      .filter((p) => Number.isFinite(p?.lng) && Number.isFinite(p?.lat)
        && Math.abs(p.lng) <= 180 && Math.abs(p.lat) <= 90)
      .map((p) => [p.lng, p.lat]);
  }, [data?.positions]);

  /* ===== DECISÃO HÍBRIDA ===== */
  useEffect(() => {
    if (!map || !gpsPath.length) {
      setPaths([]);
      return;
    }

    // Sempre mostra GPS
    setPaths([gpsPath]);
    if (gpsPath.length < 2) return;

    // Não precisa Google → custo ZERO
    if (!needsGoogleFix(gpsPath)) return;
    if (!routesLib) return;

    const sampled = samplePoints(gpsPath);
    const cacheKey = sampled.map((p) => p.join(",")).join("|");

    const cached = getCache(cacheKey);
    if (cached) {
      setPaths([cached]);
      return;
    }

    const service = new routesLib.DirectionsService();
    let cancelled = false;

    service.route(
      {
        origin: { lat: sampled[0][1], lng: sampled[0][0] },
        destination: {
          lat: sampled[sampled.length - 1][1],
          lng: sampled[sampled.length - 1][0],
        },
        waypoints: sampled.slice(1, -1).map((p) => ({
          location: { lat: p[1], lng: p[0] },
          stopover: false,
        })),
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (cancelled || status !== google.maps.DirectionsStatus.OK || !result?.routes[0]?.overview_path?.length) return;

        const path: LngLat[] =
          result.routes[0].overview_path.map((p) => [
            p.lng(),
            p.lat(),
          ]);

        setCache(cacheKey, path);
        setPaths([path]);
      }
    );
    return () => { cancelled = true; };
  }, [gpsPath, map, routesLib]);


  const [loadedModel, setLoadedModel] = useState<any>(null);

  useEffect(() => {
    let mounted = true;

    load(carModel, GLTFLoader).then((model) => {
      if (mounted) setLoadedModel(model);
    }).catch(() => {
      if (mounted) setLoadedModel(null);
    });

    return () => {
      mounted = false;
    };
  }, []);


  useEffect(() => {
    if (!map || !paths.length || !loadedModel) return;

    const overlay = new DeckOverlay({});
    overlay.setMap(map);
    const stopAnimation = startAnimation(map!!, overlay, paths, { tracking: tracking, showPaths: showPaths, play }, loadedModel);

    return () => {
      stopAnimation();
      overlay.finalize();
    };
  }, [paths, map, play, tracking, showPaths, loadedModel]);
  return <></>
};

/* =========================
   ANIMAÇÃO
========================= */

const startAnimation = (
  map: google.maps.Map,
  overlay: DeckOverlay,
  data: [number, number][][],
  options: { tracking: boolean; showPaths: boolean, play: boolean },
  carModel?: any
) => {
  const trips = data.map(waypoints => new TripBuilder({ waypoints, loop: false }))
    .filter(trip => trip.keyframes.length > 0);

  if (!trips.length) {
    overlay.setProps({ layers: [] });
    return () => {};
  }

  let timestamp = 0;
  let animation: number;

  const onAnimationFrame = () => {
    timestamp += 0.02;
    const frame = trips.map(trip => trip.getFrame(timestamp))
      .filter((item): item is Frame => item !== undefined);

    if (options.tracking && frame.length) {
      map.moveCamera({
        center: { lat: frame[0]?.point[1] ?? -15.5986686, lng: frame[0]?.point[0] ?? -56.0991301 },
        heading: frame[0]?.heading ?? 0,
        zoom: 18.5,
        tilt: 45
      });
    };

    const layers = [
      options.showPaths &&
      new PathLayer({
        id: 'trip-lines',
        data: trips,
        getPath: d => d.keyframes.map((f: any) => f.point),
        getColor: _ => [128 * Math.random(), 255 * Math.random(), 255],
        jointRounded: false,
        opacity: 1,
        getWidth: 1
      }),
      new ScenegraphLayer({
        id: 'car',
        data: frame,
        sizeScale: 1.5,
        scenegraph: carModel,
        getPosition: d => d.point,
        getTranslation: [0, 0, 1],
        getOrientation: (d: any) => {
          return [-180, 180 - (Number.isFinite(d.heading) ? d.heading : 0), 90]
        },
        _lighting: 'pbr'
      }),
    ].filter(Boolean);

    overlay.setProps({ layers });
    const hasNextFrame = trips.some(trip => timestamp < trip.keyframes[trip.keyframes.length - 1].time);
    animation = (options.play && hasNextFrame ? requestAnimationFrame(onAnimationFrame) : 0)
  };

  onAnimationFrame();
  return () => cancelAnimationFrame(animation);
};

export default LiveRoutesTracker;
